/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useBrandingList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing tenant list data fetching and state.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { fetchTenants } from '../api/brandingApi';

export function useBrandingList() {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || 'main';

  const availableColumns = useMemo(
    () => [
      { key: 'name', label: t('tenants.name'), visible: true, sortable: true },
      { key: 'logo', label: t('branding.branding'), visible: true, sortable: false },
      { key: 'isActive', label: t('branding.status'), visible: true, sortable: true },
      { key: 'createdAt', label: t('branding.created'), visible: true, sortable: true },
      { key: 'actions', label: t('branding.actions'), visible: true, sortable: false },
    ],
    [t]
  );

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const response = await fetchTenants(params, tenantSlug, user?.id ? Number(user.id) : undefined);
      return {
        data: response.data || [],
        pagination: response.pagination ? {
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          totalPages: response.pagination.pages
        } : {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    },
    [tenantSlug, user?.id]
  );

  return useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'branding',
  });
}

